/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React, { useRef } from 'react';
import {
  WMLayer,
  webmapUtils,
  LayerType,
  WMJSDimension,
} from '@opengeoweb/webmap';
import type { OnInitializeLayerProps } from '../../components/OpenLayers/utils/types';
import { useEDRGetCollectionDetails } from '../hooks';

/**
 * Returns WMLayer instance in EDR mode. The layer will contains parsed dimensions and styles and keeps a state for these properties.
 *
 * @param {string} serviceUrl
 * @param {string} name
 * @returns {(WMLayer | null)}
 */
export const useCreateEDRWMLayer = (
  serviceUrl: string,
  name: string,
  id?: string,
  onLayerError?: (layerId: string, message: string) => void,
): WMLayer | null => {
  const [layer, setLayer] = React.useState<WMLayer | null>(null);
  const refId = useRef<string>(id ?? webmapUtils.generateLayerId());
  React.useEffect((): void => {
    const layerId = refId.current;
    // Check if the layer is already registered
    const tmpWMLayer = webmapUtils.getWMLayerById(layerId);
    if (!tmpWMLayer || (tmpWMLayer.service !== serviceUrl && name.length > 0)) {
      // Make a new layer if the serviceUrl changes. (But not when the name changes)
      const wmLayer = new WMLayer({
        name,
        service: serviceUrl,
        layerType: LayerType.edrFeatureLayer,
        id: layerId,
      });
      webmapUtils.registerWMLayer(wmLayer, layerId);
    }

    // Set the name and parse the layer
    const wmLayer = webmapUtils.getWMLayerById(layerId);

    wmLayer
      .setName(name)
      .then(() => {
        setLayer(wmLayer);
      })
      .catch((e) => {
        setLayer(wmLayer);
        onLayerError?.(layerId, e);
      });
  }, [name, onLayerError, serviceUrl]);

  return layer;
};
export const useEDRWMLayer = (
  edrBaseUrl: string,
  parameter: string,
  layerId?: string,
  onInitializeLayer?: (payload: OnInitializeLayerProps) => void,
): WMLayer | null => {
  const wmLayer = useCreateEDRWMLayer(edrBaseUrl, parameter, layerId);

  const collectionInfo = useEDRGetCollectionDetails(edrBaseUrl);
  const [timeRangeDuration, setTimeRangeDuration] = React.useState<
    string | undefined
  >(undefined);

  React.useEffect(() => {
    if (wmLayer && collectionInfo.data && collectionInfo.isFetched) {
      // TODO: Extract timerange from the EDR service: https://gitlab.com/opengeoweb/geoweb-assets/-/issues/4139
      // Round start and end to not include seconds
      const intervalStart = collectionInfo.data?.extent?.temporal
        ?.interval[0][0]
        ? `${collectionInfo.data?.extent?.temporal?.interval[0][0].slice(0, 17)}00Z`
        : undefined;
      const intervalEnd = collectionInfo.data?.extent?.temporal?.interval[0][1]
        ? `${collectionInfo.data?.extent?.temporal?.interval[0][1].slice(0, 17)}00Z`
        : undefined;

      const timeRangeDurationFromEdrCollection = edrBaseUrl.includes(
        '10-minute',
      )
        ? `${intervalStart}/${intervalEnd}/PT10M`
        : `${intervalStart}/${intervalEnd}/PT1M`;

      if (!wmLayer.getDimension('time')) {
        const dim = new WMJSDimension({
          name: 'time',
          currentValue: collectionInfo.data?.extent?.temporal?.interval[0][1],
          units: 'ISO8601',
          values: timeRangeDurationFromEdrCollection,
        });

        dim.initialize();
        wmLayer.dimensions.push(dim);
      } else {
        wmLayer
          .getDimension('time')
          ?.reInitializeValues(timeRangeDurationFromEdrCollection);
      }
      setTimeRangeDuration(timeRangeDurationFromEdrCollection);
      wmLayer.isConfigured = true;
    }
  }, [collectionInfo.data, collectionInfo.isFetched, wmLayer, edrBaseUrl]);

  React.useEffect(() => {
    const initializeLayer = async (): Promise<void> => {
      if (wmLayer) {
        // Set time dimension current value based on layerprops dimension value
        const wmTimeDim = wmLayer?.dimensions?.find(
          (wmDimension) => wmDimension.name === 'time',
        );

        const timeValueToInitialize = wmTimeDim?.getValue();

        wmTimeDim &&
          timeValueToInitialize &&
          wmTimeDim.setValue(timeValueToInitialize);
        const layerProps = {
          layerId: wmLayer.id,
          dimensions: wmLayer.dimensions.map((dimension) => {
            return {
              name: dimension.name,
              units: dimension.units,
              currentValue: dimension.currentValue,
              maxValue: dimension.getLastValue(),
              minValue: dimension.getFirstValue(),
              synced: dimension.synced,
              values: dimension.values,
              unitSymbol: dimension.unitSymbol,
            };
          }),
          style: '',
        };
        onInitializeLayer?.(layerProps);
      }
    };

    timeRangeDuration &&
      initializeLayer().catch((e) => window.console.error(e));
    return (): void => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wmLayer, parameter, timeRangeDuration]);

  return wmLayer;
};
